import { useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import styled from "styled-components";
import Button from "../components/Button";
import Card from "../components/Card";
import DeleteAccountModal from "../components/DeleteAccountModal";
import { Stack } from "../components/Layout";
import PageShell from "../components/PageShell";
import { Heading, Text } from "../components/Typography";
import { apiPost, ApiError } from "../services/apiClient";

const DashboardGrid = styled.div`
  display: grid;
  gap: ${({ theme }) => theme.spacing(3)};
  grid-template-columns: 1fr;

  @media (min-width: 768px) {
    grid-template-columns: 1fr 1fr;
  }
`;

const UploadPanel = styled(Card)`
  border-style: dashed;
  border-color: ${({ theme, $active }) =>
    $active ? theme.colors.accent : theme.colors.border};
  background-color: ${({ theme, $active }) =>
    $active ? theme.colors.background : theme.colors.backgroundSecondary};
  text-align: center;
  transition:
    border-color ${({ theme }) => theme.transitions.default},
    background-color ${({ theme }) => theme.transitions.default};
`;

const GraphCard = styled(Card)`
  padding: ${({ theme }) => theme.spacing(2)};
  overflow: hidden;
`;

const GraphImage = styled.img`
  width: 100%;
  height: auto;
  border-radius: ${({ theme }) => theme.radii.control};
`;

const ChartsGrid = styled.div`
  display: grid;
  gap: ${({ theme }) => theme.spacing(3)};
  grid-template-columns: 1fr;

  @media (min-width: 768px) {
    grid-template-columns: 1fr 1fr;
  }
`;

const ErrorText = styled(Text)`
  color: ${({ theme }) => theme.colors.danger};
  font-size: ${({ theme }) => theme.typography.sizes.sm};
`;

const StatusText = styled(Text)`
  color: ${({ theme }) => theme.colors.textMuted};
  font-size: ${({ theme }) => theme.typography.sizes.sm};
`;

const FullWidth = styled.div`
  grid-column: 1 / -1;
`;

function toBackendPayload(instagramData) {
  if (!instagramData?.messages) {
    return [];
  }

  return instagramData.messages
    .filter((msg) => msg.content)
    .map((msg) => ({
      content: msg.content,
      timestamp_ms: msg.timestamp_ms,
      sender_name: msg.sender_name,
    }));
}

function InputDms({ onDataLoaded, onError, disabled }) {
  const fileInputRef = useRef(null);
  const [isDragging, setIsDragging] = useState(false);
  const [localError, setLocalError] = useState("");

  function processFile(file) {
    if (!file || disabled) {
      return;
    }

    setLocalError("");

    const isJson =
      file.name.toLowerCase().endsWith(".json") ||
      file.type === "application/json";
    if (!isJson) {
      const message = "Please upload a JSON file.";
      setLocalError(message);
      onError?.(message);
      return;
    }

    const reader = new FileReader();
    reader.onload = function (e) {
      try {
        const json = JSON.parse(e.target.result);
        onDataLoaded(json);
      } catch {
        const message = "Could not parse JSON file.";
        setLocalError(message);
        onError?.(message);
      }
    };
    reader.readAsText(file);
  }

  function handleDragAndDrop(event) {
    event.preventDefault();
    setIsDragging(false);
    processFile(event.dataTransfer.files[0]);
  }

  function handleDragOver(event) {
    event.preventDefault();
  }

  function handleDragEnter(event) {
    event.preventDefault();
    if (!disabled) {
      setIsDragging(true);
    }
  }

  function handleDragLeave(event) {
    event.preventDefault();
    setIsDragging(false);
  }

  function handleBrowseClick() {
    fileInputRef.current?.click();
  }

  function handleFileChange(event) {
    processFile(event.target.files[0]);
    event.target.value = "";
  }

  return (
    <UploadPanel
      $active={isDragging}
      padding={4}
      onDrop={handleDragAndDrop}
      onDragOver={handleDragOver}
      onDragEnter={handleDragEnter}
      onDragLeave={handleDragLeave}
    >
      <Stack $gap={2} $align="center">
        <Heading as="h2" $size="lg">
          Upload JSON file
        </Heading>
        <Text $muted $size="sm">
          Drag &amp; drop JSON file here
        </Text>
        <Button
          type="button"
          variant="secondary"
          onClick={handleBrowseClick}
          disabled={disabled}
        >
          Browse...
        </Button>
        <input
          ref={fileInputRef}
          type="file"
          accept=".json,application/json"
          onChange={handleFileChange}
          hidden
        />
        {localError && <ErrorText>{localError}</ErrorText>}
      </Stack>
    </UploadPanel>
  );
}

function UserDashboard() {
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [graphUrls, setGraphUrls] = useState(null);
  const [analyzing, setAnalyzing] = useState(false);
  const [analyzeError, setAnalyzeError] = useState("");
  const [showDeleteModal, setShowDeleteModal] = useState(false);

  async function analyzeMessages(instagramData) {
    const source = instagramData ?? data;

    if (!source?.messages?.length) {
      setAnalyzeError("Load an Instagram JSON file first.");
      return;
    }

    const payload = toBackendPayload(source);
    if (!payload.length) {
      setAnalyzeError("No messages with text content found in this file.");
      return;
    }

    setAnalyzing(true);
    setAnalyzeError("");
    setGraphUrls(null);

    try {
      const response = await apiPost("/insta_processing", payload);

      if (response?.graphs && Array.isArray(response.graphs)) {
        setGraphUrls(response.graphs);
      } else if (response?.images && Array.isArray(response.images)) {
        setGraphUrls(response.images);
      }
    } catch (error) {
      const message =
        error instanceof ApiError
          ? error.message
          : "Analysis request failed. The backend endpoint may not be ready yet.";
      setAnalyzeError(message);
    } finally {
      setAnalyzing(false);
    }
  }

  function handleDataLoaded(json) {
    setData(json);
    setGraphUrls(null);
    setAnalyzeError("");
    analyzeMessages(json);
  }

  const hasCharts = graphUrls?.length === 4;

  return (
    <PageShell>
      <Stack $gap={4}>
        <DashboardGrid>
          <Stack $gap={2}>
            <InputDms
              onDataLoaded={handleDataLoaded}
              onError={setAnalyzeError}
              disabled={analyzing}
            />
            {analyzing && <StatusText>Analyzing...</StatusText>}
            {analyzeError && <ErrorText>{analyzeError}</ErrorText>}
          </Stack>

          <Card padding={4}>
            <Stack $gap={2}>
              <Heading as="h2" $size="lg">
                Actions
              </Heading>
              <Button type="button" onClick={() => navigate("/DevPage")}>
                Dev tools
              </Button>
              <Button
                type="button"
                variant="secondary"
                onClick={() => setShowDeleteModal(true)}
              >
                Delete account
              </Button>
              <Button type="button" variant="ghost" title="Coming soon">
                Terms of service
              </Button>
            </Stack>
          </Card>
        </DashboardGrid>

        {hasCharts && (
          <FullWidth>
            <Stack $gap={3}>
              <GraphCard>
                <GraphImage
                  src={graphUrls[0]}
                  alt="Total Message Analysis"
                />
              </GraphCard>
              <ChartsGrid>
                <GraphCard>
                  <GraphImage
                    src={graphUrls[1]}
                    alt="Positive Messages From You Over Time"
                  />
                </GraphCard>
                <GraphCard>
                  <GraphImage
                    src={graphUrls[2]}
                    alt="Positive Messages From Others Over Time"
                  />
                </GraphCard>
                <GraphCard>
                  <GraphImage
                    src={graphUrls[3]}
                    alt="Sentiment Distribution"
                  />
                </GraphCard>
              </ChartsGrid>
            </Stack>
          </FullWidth>
        )}
      </Stack>

      {showDeleteModal && (
        <DeleteAccountModal
          onClose={() => setShowDeleteModal(false)}
          onDeleted={() => navigate("/SignIn", { replace: true })}
        />
      )}
    </PageShell>
  );
}

export default UserDashboard;
